// 설비 가동 모니터링 + OEE
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Counter } from '../components/Counter'
import { Skeleton } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

interface EqStatus {
  equipment_id: string
  equipment_name: string
  operation_rate: number
  performance_rate: number
  quality_rate: number
  oee: number
  reported_at: string
}

function Bar({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <div className="row space-between" style={{ marginBottom: 4 }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
        <span className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{value.toFixed(1)}%</span>
      </div>
      <div role="progressbar" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={100} aria-label={label}
        style={{ height: 8, borderRadius: 999, background: 'var(--color-line)' }}>
        <div style={{
          width: `${value}%`,
          height: '100%', borderRadius: 999,
          background: 'linear-gradient(90deg, var(--color-accent), var(--color-accent-2))',
        }} />
      </div>
    </div>
  )
}

export function EquipmentStatusPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const [rows, setRows] = useState<EqStatus[] | null>(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const d = await api.get<EqStatus[]>('/equipment/status')
        if (active) setRows(d)
      } catch (e: any) {
        if (active) toast.push('error', e?.message ?? t('toast.fetchFailed'))
      }
    }
    load()
    const id = setInterval(load, 15000)
    return () => { active = false; clearInterval(id) }
  }, [toast, t])

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="chart-line" /> {t('eqs.title')}
      </h1>
      <p className="muted reveal">{t('eqs.intro')}</p>

      <section
        aria-label={t('eqs.aria.cards')}
        className="reveal"
        style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))' }}
      >
        {!rows
          ? Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} glass tilt={false} spotlight={false}>
                <Skeleton h={28} />
                <div style={{ height: 12 }} />
                <Skeleton h={56} />
              </Card>
            ))
          : rows.map((r) => (
              <Card key={r.equipment_id} glass tilt spotlight>
                <div className="row space-between">
                  <span className="badge info">{r.equipment_id}</span>
                  <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                    {new Date(r.reported_at).toLocaleTimeString('ko-KR')}
                  </span>
                </div>
                <h3 style={{ marginBlock: '0.3rem 0.6rem' }}>{r.equipment_name}</h3>
                <p style={{ fontSize: 'var(--fs-3xl)', margin: 0, lineHeight: 1, fontFamily: 'var(--font-display)', fontWeight: 800 }}>
                  <Counter value={r.oee} decimals={1} suffix="%" />
                </p>
                <p className="muted" style={{ marginTop: 4 }}>{t('eqs.metric.oee')}</p>
                <div className="stack" style={{ marginTop: 12 }}>
                  <Bar value={r.operation_rate} label={t('eqs.metric.operation')} />
                  <Bar value={r.performance_rate} label={t('eqs.metric.performance')} />
                  <Bar value={r.quality_rate} label={t('eqs.metric.quality')} />
                </div>
              </Card>
            ))}
      </section>
    </main>
  )
}
